import { Message, Conversation as TwilioConversation } from '@twilio/conversations';
import { UserInfo } from 'src/app/interfaces';
import { EntityStatus } from 'src/app/interfaces/views.interface';
import { AccountStatus, Conversation } from 'src/app/swagger-api';
import { UserBasic } from 'src/app/swagger-api/model/userBasic';
import { MessengerMessage } from '../../event-messenger/interfaces/messenger.interface';
import { ChatChannelType, ChatConversation } from '../interfaces/chat-model.interface';
import { TwilioConversationAttributes } from '../services/twilio/twilio.interfaces';

/**
 * @description Searches for conversation in conversations list by passed unique name. Returns
 *              null in case conversations list is undefined or doesn't have
 *              a conversation with such unique name.
 * @param { string } uniqueName
 * @param { ChatConversation[] } conversations
 * @returns { ChatConversation }
 */
export function findConversationByUniqueName(
  uniqueName: string,
  conversations: ChatConversation[],
): ChatConversation | null {
  return conversations
    ? conversations.find((conversation: ChatConversation) => conversation.channel.channel_external_id === uniqueName)
    : null;
}

/**
 * @description Searches for conversation in conversations list by passed id. Returns
 *              null in case conversations list is undefined or doesn't have
 *              a conversation with such id.
 * @param { string } id
 * @param { ChatConversation[] } conversations
 * @returns { ChatConversation }
 */
export function findConversationById(id: string, conversations: ChatConversation[]): ChatConversation | null {
  return conversations
    ? conversations.find((conversation: ChatConversation) => conversation.relatedEntityId === id)
    : null;
}

/**
 * @description Sorts the conversations list by date time.
 * @param { ChatConversation[] } conversations
 * @returns { ChatConversation[] }
 */
export function sortConversationsByLastMessageDateTime(conversations: ChatConversation[]): ChatConversation[] {
  const conversationsWithoutMessages: ChatConversation[] = conversations.filter(
    (conversation: ChatConversation) => !conversation.lastMessage,
  );
  const conversationsWithMessages: ChatConversation[] = conversations
    .filter((conversation: ChatConversation) => conversation.lastMessage)
    .sort(
      (a: ChatConversation, b: ChatConversation) =>
        new Date(b.lastMessage.dateCreated).getTime() - new Date(a.lastMessage.dateCreated).getTime(),
    );
  return conversationsWithMessages.concat(conversationsWithoutMessages);
}

/**
 * @description Searches the interlocutor from conversation participants that have not
 *              the specified userId.
 * @param { ChatConversation } conversation
 * @param { string } userId
 * @returns { UserInfo }
 */
export function getInterlocutorInternal(conversation: ChatConversation, userId: string): UserInfo | null {
  const userInfo: UserInfo | undefined = conversation.participatingUsers.find(
    (user: UserInfo) => user.userId !== userId,
  );
  return userInfo ? userInfo : null;
}

/**
 * @description Searches the interlocutor from conversation participants that have not
 *              the specified userId.
 * @param { ConversationBasic } conversation
 * @param { string } userId
 * @returns { UserBasic }
 */
export function getInterlocutor(conversation: Conversation, userId: string): UserBasic | null {
  const userBasic: UserBasic | undefined = conversation.participants.find((user: UserBasic) => user.user_id !== userId);
  return userBasic ? userBasic : null;
}

export function getChannelLastMessage(index: number, messages: Message[]): Message {
  return messages[index];
}

export function sortMessagesByCreationTime(messages: Message[]): Message[] {
  const sortedMessages: Message[] = [...messages].sort((m1: Message, m2: Message) => {
    const mDate1: Date = m1.dateCreated;
    const mDate2: Date = m2.dateCreated;
    if (mDate1 > mDate2) {
      return -1;
    }
    if (mDate1 < mDate2) {
      return 1;
    }
    return 0;
  });
  return sortedMessages;
}

export function sortUsersByName(participants: UserInfo[]): UserInfo[] {
  return participants.sort((a: UserInfo, b: UserInfo) => {
    if (a.userName < b.userName) {
      return -1;
    }
    if (a.userName > b.userName) {
      return 1;
    }
    return 0;
  });
}

export function getChannelType(twilioConversation: TwilioConversation): ChatChannelType {
  const conversationAttributes: TwilioConversationAttributes =
    twilioConversation.attributes as TwilioConversationAttributes;
  return conversationAttributes.type || ChatChannelType.CONVERSATION;
}

export function isNotificationChannel(conversation: TwilioConversation): boolean {
  return getChannelType(conversation) === ChatChannelType.NOTIFICATION;
}

export function messageBelongsToNotificationChannel(message: Message): boolean {
  return getChannelType(message.conversation) === ChatChannelType.NOTIFICATION;
}

export function sortMessengerMessagesByCreationTime(messages: MessengerMessage[]): MessengerMessage[] {
  const sortedMessages: MessengerMessage[] = [...messages].sort((m1: MessengerMessage, m2: MessengerMessage) => {
    const mDate1: number = m1.dateCreated.getTime();
    const mDate2: number = m2.dateCreated.getTime();
    if (mDate1 > mDate2) {
      return -1;
    }
    if (mDate1 < mDate2) {
      return 1;
    }
    return 0;
  });
  return sortedMessages;
}

export function isGroupConversation(conversation: ChatConversation): boolean {
  return conversation.participatingUsers.length > 2;
}

export function isPrivateConversation(conversation: ChatConversation): boolean {
  return conversation.participatingUsers.length === 2;
}

export function isReadOnlyConversation(conversation: ChatConversation, userId: string): boolean {
  return (
    isPrivateConversationWithDeletedUser(conversation, userId) ||
    isPrivateConversationWithDeactivatedUser(conversation, userId)
  );
}

export function isPrivateConversationWithDeletedUser(conversation: ChatConversation, userId: string): boolean {
  if (isPrivateConversation(conversation)) {
    const interlocutorUser: UserInfo | null = getInterlocutorInternal(conversation, userId);
    if (interlocutorUser) {
      return interlocutorUser.userStatus === AccountStatus.DELETED;
    }
  }
  return false;
}

export function isPrivateConversationWithDeactivatedUser(conversation: ChatConversation, userId: string): boolean {
  if (isPrivateConversation(conversation)) {
    const interlocutorUser: UserInfo | null = getInterlocutorInternal(conversation, userId);
    if (interlocutorUser) {
      return interlocutorUser.userStatus === AccountStatus.DEACTIVATED;
    }
  }
  return false;
}

export function getPrivateConversationInterlocutorAccountStatus(
  conversation: ChatConversation,
  userId: string,
): EntityStatus | null {
  if (isPrivateConversation(conversation)) {
    const interlocutorUser: UserInfo | null = getInterlocutorInternal(conversation, userId);
    if (interlocutorUser) {
      return interlocutorUser.userStatus;
    }
  }
  return null;
}

export class AccountStatusChat {
  public static ACCOUNT_DEACTIVATED_TEXT = 'This user is deactivated.';
  public static ACCOUNT_DELETED_TEXT = 'There is no such user in the system anymore!';
}
